import { dateOf, holdInUtc } from "@repo/helpers";
import { KeyedArray } from "@repo/models";
import { EditableGridPage, Loading } from "@repo/ui";
import { useCallback, useMemo } from "react";

import { useBackgroundCheckQueries } from "../../api/backgroundCheckApi";
import { useDocumentationApi } from "../../api/documentationApi";
import { useDrugTestApi } from "../../api/drugTestApi";
import { useRepOnboardingAuditApi } from "../../api/repOnboardingAuditApi";
import {
	BackgroundCheckProvider,
	DocumentationProvider,
	DrugTestsProvider,
	RepOnboardingAuditProvider,
	useBackgroundCheckContext,
	useDocumentationContext,
	useDrugTestsContext,
	useRepContext,
	useRepOnboardingAuditContext,
} from "../../contexts/editableItemContext";
import { useUserContext } from "../../contexts/userContext";
import { usePermissionsCheck } from "../../helpers/permissions";
import { useEntityId, useUpdateRoute } from "../../helpers/urlHooks";
import { RepOnboardingAudit } from "../../models";
import OnboardingAuditEditorDynamic from "./editor";
import RepOnboardingAuditGridBasic from "./grid";

function OnboardingAudits() {
	return (
		<RepOnboardingAuditProvider>
			<BackgroundCheckProvider>
				<DrugTestsProvider>
					<DocumentationProvider>
						<OnboardingAuditsInternal />
					</DocumentationProvider>
				</DrugTestsProvider>
			</BackgroundCheckProvider>
		</RepOnboardingAuditProvider>
	);
}

function OnboardingAuditsInternal() {
	const { user } = useUserContext();
	const { repId, rep, repLoaded } = useRepContext();
	const { overwriteItem: overwriteOnboardingAudit, pendingChanges } = useRepOnboardingAuditContext();
	const { item: currentBackgroundCheck, overwriteItem: overwriteBackgroundCheck } = useBackgroundCheckContext();
	const { item: currentDrugTest, overwriteItem: overwriteDrugTest } = useDrugTestsContext();
	const { item: currentDocumentation, overwriteItem: overwriteDocumentation } = useDocumentationContext();

	const { useRepOnboardingAuditsQuery } = useRepOnboardingAuditApi();
	const { useBackgroundCheckQuery } = useBackgroundCheckQueries();
	const { useDrugTestQuery } = useDrugTestApi();
	const { useDocumentationQuery } = useDocumentationApi();
	const { data: audits, loaded: auditsLoaded } = useRepOnboardingAuditsQuery(repId);
	const { data: allBackgroundChecks, loaded: backgroundChecksLoaded } = useBackgroundCheckQuery();
	const { data: allDrugTests, loaded: drugTestsLoaded } = useDrugTestQuery(repId);
	const { data: documentation } = useDocumentationQuery(repId);
	const entityId = useEntityId();
	const updateRoute = useUpdateRoute();

	const { fullAPReadOnly } = usePermissionsCheck();

	const loading = useMemo(() => {
		return !repLoaded || !auditsLoaded || !backgroundChecksLoaded || !drugTestsLoaded || !documentation;
	}, [repLoaded, auditsLoaded, backgroundChecksLoaded, drugTestsLoaded, documentation]);

	const handleOpen = useCallback(
		(item: RepOnboardingAudit) => {
			overwriteOnboardingAudit(item);
		},
		[overwriteOnboardingAudit],
	);

	const openItem = useCallback(
		(item: RepOnboardingAudit) => {
			function updateContext<T>(
				id: number,
				currentItem: T,
				allItems: KeyedArray<T>,
				overwrite: (item: T) => void,
				item?: T,
			) {
				const newItem = item ?? allItems?.get(id);
				if (newItem && !currentItem) overwrite({ ...newItem });
			}

			overwriteOnboardingAudit(item);
			updateContext(
				item.backgroundCheck.backgroundCheckId,
				currentBackgroundCheck,
				allBackgroundChecks,
				overwriteBackgroundCheck,
			);
			updateContext(item.drugTest.drugTestId, currentDrugTest, allDrugTests, overwriteDrugTest);
			updateContext(null, currentDocumentation, null, overwriteDocumentation, documentation);
		},
		[
			overwriteOnboardingAudit,
			overwriteBackgroundCheck,
			overwriteDrugTest,
			overwriteDocumentation,
			currentBackgroundCheck,
			allBackgroundChecks,
			currentDrugTest,
			allDrugTests,
			currentDocumentation,
			documentation,
		],
	);

	const newItem = useCallback(() => {
		function getLatestItem<T>(allItems: KeyedArray<T>, sortKey: string) {
			return allItems?.sort((a, b) => b[sortKey] - a[sortKey]).data[0];
		}

		const backgroundCheck = getLatestItem(allBackgroundChecks, "requestDate");
		const drugTest = getLatestItem(allDrugTests, "conductedOn");

		openItem({
			representativeId: repId,
			qaAnalyst: user.userName,
			qaAnalystId: user.userId,
			onboardingDate: holdInUtc(rep.startDate),
			auditDate: new Date(dateOf(new Date())),
			backgroundCheck: {
				backgroundCheckId: backgroundCheck?.backgroundCheckId,
				investigationId: backgroundCheck?.investigationReportId && `${backgroundCheck?.investigationReportId}`,
			},
			drugTest: {
				drugTestId: drugTest?.drugTestId,
				specimenId: drugTest?.specimenId,
			},
			nrgConsentForm: {},
			performanceAndStandards: {},
			trainingExam: {},
		} as RepOnboardingAudit);
	}, [openItem, repId, rep, allBackgroundChecks, allDrugTests]);

	return (
		<Loading loading={loading}>
			<EditableGridPage<RepOnboardingAudit>
				pendingChanges={pendingChanges}
				items={audits}
				keyProperty="representativeOnboardingAuditId"
				id="RepOnboardingAuditsContainer"
				openItem={handleOpen}
				newItem={newItem}
				Editor={OnboardingAuditEditorDynamic}
				Grid={RepOnboardingAuditGridBasic}
				itemsLoaded={auditsLoaded}
				entityId={entityId}
				updateRoute={updateRoute}
				fullReadOnly={fullAPReadOnly}
			/>
		</Loading>
	);
}

export default OnboardingAudits;
